import { Theme } from '@mui/material';

import { Box, styled } from '@/shared/ui';
import { CustomTypography } from '@/shared/ui/Typography';

import { TNavigationItemVariants, TStyledLabelProps } from '../types';

interface StyledProps {
  variant: TNavigationItemVariants;
  disabled?: boolean;
}

const hoverCircle = (theme: Theme) => ({
  transform: 'scale(1.3)',
  transformOrigin: 'top',
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.common.white,
  borderColor: 'transparent',
  marginBottom: '15px',
});

const disabledCircle = (theme: Theme) => ({
  transform: 'scale(1)',
  backgroundColor: theme.palette.custom.grey2,
  color: theme.palette.custom.black[50],
  borderColor: theme.palette.custom.grey2,
  marginBottom: 0,
});

const hoverSquare = (theme: Theme) => ({
  color: theme.palette.common.white,
  borderColor: theme.palette.custom.grey7,
  backgroundColor: theme.palette.primary.main,
});

const disabledSquare = (theme: Theme) => ({
  color: theme.palette.custom.black[50],
  borderColor: theme.palette.custom.grey2,
  backgroundColor: theme.palette.custom.grey2,
  boxShadow: 'none',
});

const hoverStandard = (theme: Theme) => ({
  backgroundColor: theme.palette.common.white,
});

export const StyledContainer = styled(Box)<StyledProps>(
  ({ theme, variant, disabled }) => ({
    display: 'flex',
    flexDirection: variant === 'standard' ? 'row' : 'column',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'height 0.5s ease',
    marginBottom: variant === 'standard' ? '5px' : '15px',
    borderRadius: variant === 'standard' ? '20px' : 0,
    padding: variant === 'standard' ? '10px' : 0,
    cursor: disabled ? 'not-allowed' : 'pointer',

    ...(variant === 'circle' && {
      width: '71px',
      '&:hover': !disabled
        ? { '.navigation-icon-container': hoverCircle(theme) }
        : {},
      '&.active': {
        '.navigation-icon-container': hoverCircle(theme),
      },
    }),

    ...(variant === 'square' && {
      width: '79px',
      '&:hover': !disabled
        ? { '.navigation-icon-container': hoverSquare(theme) }
        : {},
      '&.active': {
        '.navigation-icon-container': hoverSquare(theme),
      },
    }),

    ...(variant === 'standard' && {
      '&:hover': { ...hoverStandard(theme) },
      '.active &': {
        color: theme.palette.common.white,
        backgroundColor: theme.palette.primary.main,
      },
    }),
  })
);

export const StyledIconContainer = styled(Box)<StyledProps>(
  ({ theme, variant, disabled }) => ({
    backgroundColor: theme.palette.common.white,
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition:
      'background-color 0.3s ease, transform 0.3s ease, margin 0.3s ease',
    opacity: disabled ? 0.5 : 1,
    transform: 'scale(1)',
    transformOrigin: 'top',
    marginBottom: '0',

    ...(variant === 'circle' && {
      padding: '14.5px',
      width: '54px',
      height: '54px',
      borderRadius: '50%',
      border: `1px solid ${theme.palette.custom.grey2}`,

      ...(!disabled && {
        '&:hover': hoverCircle(theme),
        '.active &': hoverCircle(theme),
      }),

      ...(disabled && disabledCircle(theme)),
    }),

    ...(variant === 'square' && {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '79px',
      height: '71px',
      borderRadius: '20px',
      border: `1px solid ${theme.palette.custom.grey2}`,
      color: theme.palette.primary.main,
      boxShadow: `0px 8px 19px -6px ${theme.palette.custom.opacityBlack25}`,

      svg: {
        scale: '1.8',
      },

      ...(!disabled && {
        '&:hover': hoverSquare(theme),
        '.active &': hoverSquare(theme),
      }),

      ...(disabled && disabledSquare(theme)),
    }),

    ...(variant === 'standard' && {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'transparent',

      ...(!disabled && {
        '&:hover': {
          color: theme.palette.primary.main,
        },
      }),

      '.active &': {
        color: disabled
          ? theme.palette.custom.black[50]
          : theme.palette.common.white,
      },
    }),
  })
);

export const StyledLabel = styled(CustomTypography, {
  shouldForwardProp: (prop) => prop !== 'itemVariant',
})<TStyledLabelProps>(({ theme, itemVariant, disabled }) => ({
  color: theme.palette.custom.black[50],
  lineHeight: '100%',
  verticalAlign: 'middle',
  cursor: disabled ? 'not-allowed' : 'pointer',

  ...(itemVariant !== 'standard' && {
    paddingTop: '5px',
    textAlign: 'center',

    transition: 'opacity 0.5s ease',

    '.navigation-icon-container:hover ~ &, .active &': {
      opacity: '1',
      display: 'initial',
    },
  }),

  ...(itemVariant === 'standard' && {
    fontSize: '14px',
    fontWeight: 400,
    padding: '0 3px 0 8px',

    '.active &': {
      color: theme.palette.common.white,
    },
  }),
}));
